import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Box,
  Typography,
  Chip,
  Tooltip
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import { useApi } from "../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import useNotification from "../../../../shared/hooks/useNotification";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";

interface TcPdfUploaderModalProps {
  open: boolean;
  onClose: () => void;
  tcId: string;
  tcNo: string;
  tcVersionNo: string;
  tcNoPdfUrl?: string;
  tcVersionNoPdfUrl?: string;
  tcUploadedFiles?: Array<{ fileName: string; blobUrl: string; uploadedDate: string }>;
  submissionStatus: string;
  cbLicensingCode: string;
  tcDateOfIssue?: string;
  onUploadSuccess: (responseData?: any) => void;
}

export const TcPdfUploaderModal: React.FC<TcPdfUploaderModalProps> = ({
  open,
  onClose,
  tcId,
  tcNo,
  tcVersionNo,
  tcNoPdfUrl,
  tcVersionNoPdfUrl,
  tcUploadedFiles,
  submissionStatus,
  cbLicensingCode,
  tcDateOfIssue,
  onUploadSuccess,
}) => {
  const { executePost } = useApi();
  const { notify } = useNotification();
  const [selectedFileA, setSelectedFileA] = useState<File | null>(null);
  const [selectedFileB, setSelectedFileB] = useState<File | null>(null);
  const [errorA, setErrorA] = useState<string | null>(null);
  const [errorB, setErrorB] = useState<string | null>(null);
  const [successA, setSuccessA] = useState<boolean>(false);
  const [successB, setSuccessB] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Backend may send Python None serialised as the string "None" — treat it as absent.
  const validIssueDate = tcDateOfIssue && tcDateOfIssue.toLowerCase() !== 'none' ? tcDateOfIssue : undefined;
  const dateStr = validIssueDate ? moment.utc(validIssueDate).format('YYYYMMDD') : '';

  const isBaseAvailable = !!tcNoPdfUrl;
  // When file history is available, verify the CURRENT tcVersionNo has been uploaded — not just an old amendment.
  // Falls back to trusting tcVersionNoPdfUrl when history is absent.
  const isCurrentAmendmentUploaded = tcNo !== tcVersionNo && (
    tcUploadedFiles
      ? tcUploadedFiles.some(f => f.fileName.toLowerCase().includes(tcVersionNo.toLowerCase()))
      : !!tcVersionNoPdfUrl
  );
  const isVersionAvailable = isCurrentAmendmentUploaded;
  const hasNoAmendments = tcNo === tcVersionNo;
  const [viewSlotUrl, setViewSlotUrl] = useState<string | null>(null);
  const isBothAvailable = isBaseAvailable && isVersionAvailable;
  const isMissingIssueDate = !validIssueDate;
  const isUploadDisabled = isBothAvailable || (isBaseAvailable && hasNoAmendments) || isMissingIssueDate;

  // Normalize CB code: "CB-CUC" → "cb-cuc", "CUC" → "cb-cuc"
  const normalizedCb = cbLicensingCode.toLowerCase();
  const expectedCb = normalizedCb.startsWith('cb-') ? normalizedCb : `cb-${normalizedCb}`;

  // Template names omit the .pdf extension — the OS file-save dialog appends it automatically.
  const templateNameA = `tc_${expectedCb}_${tcNo.toLowerCase()}_${dateStr}`;

  // When tcNo === tcVersionNo the TC has no amendments (Slot B is locked), so displayTcVersionNo
  // is unused in practice. When they differ, tcVersionNo IS the amended version — trust it directly.
  const displayTcVersionNo = tcVersionNo || tcNo;
  const templateNameB = `tc_${expectedCb}_${displayTcVersionNo.toLowerCase()}_${dateStr}`;

  // Resolve the viewable URL for the versioned slot — prefer the prop, fall back to file history.
  const versionPdfViewUrl = tcVersionNoPdfUrl ||
    tcUploadedFiles?.find(f => f.fileName.toLowerCase().includes(tcVersionNo.toLowerCase()))?.blobUrl;

  const getVersionSuffix = (ver: string) => {
    const parts = ver.split("-");
    const lastPart = parts[parts.length - 1];
    return /^\d+$/.test(lastPart) ? `-${lastPart}` : "-01+";
  };
  const versionSuffix = getVersionSuffix(displayTcVersionNo);

  const handleClose = () => {
    if (uploading) return;
    setSelectedFileA(null);
    setSelectedFileB(null);
    setErrorA(null);
    setErrorB(null);
    setSuccessA(false);
    setSuccessB(false);
    onClose();
  };

  const copyTemplateA = () => {
    navigator.clipboard.writeText(templateNameA);
    notify('Base Certificate filename copied (add .pdf extension when saving)', 'success');
  };
  const copyTemplateB = () => {
    navigator.clipboard.writeText(templateNameB);
    notify('Versioned Certificate filename copied (add .pdf extension when saving)', 'success');
  };

  const validateFileName = (file: File, targetSlot?: 'A' | 'B'): { valid: boolean; slot: 'A' | 'B'; error: string | null } => {
    const filename = file.name;

    // Block re-upload of base if already available
    const extractedMiddlePart = filename.match(/^tc_([a-zA-Z0-9\-]+)_([a-zA-Z0-9\-.]+)_(\d{8})\.[pP][dD][fF]$/)?.[2]?.toLowerCase() || '';
    if (extractedMiddlePart === tcNo.toLowerCase()) {
      if (isBaseAvailable) {
        return {
          valid: false,
          slot: 'A',
          error: "Base certificate is already uploaded. You only need to upload versioned amendment certificates."
        };
      }
    }

    // History duplicate check
    const uploadedFiles = tcUploadedFiles || [];
    const duplicateInDb = uploadedFiles.some((f: any) => f.fileName === filename);
    if (duplicateInDb) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `File already exists: '${filename}' has already been uploaded for this Transaction Certificate.`
      };
    }

    // Cross-slot session check
    if (targetSlot === 'A' && selectedFileB && selectedFileB.name === filename) {
      return { valid: false, slot: 'A', error: "File already selected: This file is already chosen for the Amended Certificate slot." };
    }
    if (targetSlot === 'B' && selectedFileA && selectedFileA.name === filename) {
      return { valid: false, slot: 'B', error: "File already selected: This file is already chosen for the Original Certificate slot." };
    }

    const pattern = /^tc_([a-zA-Z0-9\-]+)_([a-zA-Z0-9\-.]+)_(\d{8})\.[pP][dD][fF]$/;
    const match = filename.match(pattern);

    if (!match) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: "Invalid filename format. Expected structure: tc_[cb-code]_[tcNo/tcVersionNo]_[date_of_issue].pdf"
      };
    }

    const [_, extractedCb, extractedTcVer, extractedDateStr] = match;

    // CB code check
    if (extractedCb.toLowerCase() !== expectedCb) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Certification Body mismatch. Filename contains '${extractedCb}' but expected '${expectedCb}'.`
      };
    }

    // Date validity check
    const year = parseInt(extractedDateStr.substring(0, 4));
    const month = parseInt(extractedDateStr.substring(4, 6));
    const day = parseInt(extractedDateStr.substring(6, 8));

    if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Invalid date '${extractedDateStr}' in filename. Please provide a valid calendar date in YYYYMMDD format.`
      };
    }

    const dateObj = new Date(year, month - 1, day);
    if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Invalid date '${extractedDateStr}' in filename. Please provide a valid calendar date.`
      };
    }

    if (!validIssueDate) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: "Transaction Certificate is missing its date of issue. Please contact support."
      };
    }

    const expectedDateStr = moment.utc(validIssueDate).format('YYYYMMDD');
    if (extractedDateStr !== expectedDateStr) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Date mismatch: filename uses '${extractedDateStr}' but the TC issue date is '${expectedDateStr}'. Please rename your file to use the date shown in the Suggested File Name above.`
      };
    }

    // Slot classification
    const cleanExtracted = extractedTcVer.toLowerCase();
    const cleanTcNo = tcNo.toLowerCase();
    const cleanDisplayVersion = displayTcVersionNo.toLowerCase();

    if (targetSlot) {
      if (targetSlot === 'A') {
        if (cleanExtracted !== cleanTcNo) {
          return { valid: false, slot: 'A', error: `Base Certificate mismatch. Filename contains '${extractedTcVer}' but expected base tcNo '${tcNo}'.` };
        }
        return { valid: true, slot: 'A', error: null };
      } else {
        if (cleanExtracted !== cleanDisplayVersion) {
          return { valid: false, slot: 'B', error: `Versioned Certificate mismatch. Filename contains '${extractedTcVer}' but expected '${displayTcVersionNo}'.` };
        }
        return { valid: true, slot: 'B', error: null };
      }
    }

    if (cleanExtracted === cleanTcNo) {
      return { valid: true, slot: 'A', error: null };
    } else {
      if (cleanExtracted !== cleanDisplayVersion) {
        return { valid: false, slot: 'B', error: `Versioned Certificate mismatch. Filename contains '${extractedTcVer}' but expected '${displayTcVersionNo}'.` };
      }
      return { valid: true, slot: 'B', error: null };
    }
  };

  const handleFilesSelect = (files: FileList | File[], targetSlot?: 'A' | 'B') => {
    const fileList = Array.from(files);
    if (fileList.length === 0) return;

    fileList.forEach(file => {
      if (file.type !== 'application/pdf') {
        notify(`File '${file.name}' is not a PDF. Only PDF files are allowed.`, 'error');
        return;
      }

      const result = validateFileName(file, targetSlot);
      if (result.slot === 'A') {
        if (result.valid) { setSelectedFileA(file); setErrorA(null); setSuccessA(true); }
        else { setSelectedFileA(null); setErrorA(result.error); setSuccessA(false); notify(`Validation failed for Slot A: ${result.error}`, 'error'); }
      } else {
        if (hasNoAmendments) {
          notify('Upload ignored: Amended slot is locked as no versioned amendments are available yet.', 'error');
          return;
        }
        if (result.valid) { setSelectedFileB(file); setErrorB(null); setSuccessB(true); }
        else { setSelectedFileB(null); setErrorB(result.error); setSuccessB(false); notify(`Validation failed for Slot B: ${result.error}`, 'error'); }
      }
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) handleFilesSelect(e.dataTransfer.files);
  };

  const browsePdf = (slot?: 'A' | 'B') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    if (!slot) input.multiple = true;
    input.onchange = (e: any) => { if (e.target.files) handleFilesSelect(e.target.files, slot); };
    input.click();
  };

  const handleFileSubmit = async () => {
    if (!selectedFileA && !selectedFileB) {
      notify('Please select at least one file to upload', 'error');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      if (selectedFileA) formData.append('files', selectedFileA);
      if (selectedFileB) formData.append('files', selectedFileB);
      formData.append('tc_id', tcId);
      formData.append('tc_no', tcNo);
      formData.append('cb_id', cbLicensingCode);
      formData.append('status', submissionStatus);
      formData.append('tc_version_no', displayTcVersionNo);
      // Append custom headers as form fields to bypass CORS preflight blocking
      formData.append('te_cb_code', cbLicensingCode);
      formData.append('te_ui_source_key', 'TrackitPortalUI-DevKey');

      const result = await executePost(
        TCAppConfig.POST_TC_MULTIPART,
        formData,
        {
          headers: {
            'Te-Cb-Code': cbLicensingCode,
            'Te-Ui-Source-Key': 'TrackitPortalUI-DevKey',
          },
        }
      );

      if (result?.status === 200) {
        notify('PDF(s) uploaded successfully', 'success');
        onUploadSuccess(result?.data);
        handleClose();
      } else {
        console.error('[TC PDF Upload] Non-200 response:', { status: result?.status, data: result?.data });

        let errMsg = 'Error uploading PDF(s). Check browser console for details.';
        if (result?.data) {
          const data = result.data;
          let backendMsg = data.message || (data.error && data.error.message) || null;
          if (backendMsg && backendMsg.includes("Downstream failure:")) {
            const jsonPart = backendMsg.replace("Downstream failure:", "").trim();
            try {
              const parsed = JSON.parse(jsonPart);
              if (parsed?.file_upload?.failed_files?.length > 0) backendMsg = parsed.file_upload.failed_files[0].error;
              else if (parsed?.message) backendMsg = parsed.message;
            } catch (_) {}
          }
          const failedFiles = data.file_upload?.failed_files || [];
          if (failedFiles.length > 0) backendMsg = failedFiles[0].error || failedFiles[0].message || backendMsg;
          if (backendMsg) errMsg = backendMsg;
        }
        notify(errMsg, 'error');
      }
    } catch (err: any) {
      console.error('[TC PDF Upload] Request exception:', { message: err?.message, responseData: err?.response?.data });
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error uploading PDF(s). Check browser console for details.';
      notify(errMsg, 'error');
    } finally {
      setUploading(false);
    }
  };

  // Only treat an error as blocking if the slot it belongs to is actually in play.
  // When Slot A is already uploaded, errorA might be stale (from a rejected re-upload drag).
  // When Slot B is locked (hasNoAmendments), errorB is irrelevant.
  const hasActiveErrorA = !!errorA && !isBaseAvailable;
  const hasActiveErrorB = !!errorB && !isVersionAvailable && !hasNoAmendments;

  const getUploadButtonTooltip = (): string => {
    if (isMissingIssueDate) return "Issue date is not set for this TC — PDF upload requires a valid date of issue to generate the correct filename";
    if (isBothAvailable) return "Both PDFs are already uploaded — no further uploads needed";
    if (isBaseAvailable && hasNoAmendments) return "Original PDF is uploaded and no amendments exist — no further uploads needed";
    if (hasActiveErrorA && hasActiveErrorB) return "Both slots have invalid files — choose the correct files or remove the wrong ones";
    if (hasActiveErrorA) return "Slot A has an invalid file — choose the correct file or remove it to proceed";
    if (hasActiveErrorB) return "Slot B has an invalid file — choose the correct file or remove it to proceed";
    if (!selectedFileA && !selectedFileB) return "Select at least one PDF file to enable upload";
    return "";
  };

  const isButtonDisabled = (!selectedFileA && !selectedFileB) || uploading || isUploadDisabled || hasActiveErrorA || hasActiveErrorB;

  const renderSlot = (
    slot: 'A' | 'B',
    isAvailable: boolean,
    selectedFile: File | null,
    error: string | null,
    success: boolean,
    title: string,
    templateName: string,
    formatHint: string,
    onCopy: () => void,
    onRemove: () => void,
    alreadyUploadedMessage: string,
    alreadyUploadedNote: string,
    isLocked?: boolean,
    pdfUrl?: string,
  ) => (
    <Grid item xs={12} md={6}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3.5,
          borderColor: isLocked
            ? 'rgba(44, 71, 72, 0.1)'
            : (isAvailable ? '#2d6a4f' : (error ? '#d32f2f' : (success ? '#2d6a4f' : 'rgba(44, 71, 72, 0.15)'))),
          backgroundColor: isLocked ? '#F4F7F7' : '#FFFFFF',
          opacity: isLocked ? 0.75 : 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.01)'
        }}
      >
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isLocked ? 'rgba(44, 71, 72, 0.5)' : '#2C4748' }}>
              {title}
            </Typography>
            {isLocked ? (
              <Chip label="Locked" size="small" variant="outlined" sx={{ borderStyle: 'dashed', color: 'rgba(44, 71, 72, 0.4)', borderColor: 'rgba(44, 71, 72, 0.3)' }} />
            ) : isAvailable ? (
              <Chip label="Uploaded" color="success" size="small" sx={{ fontWeight: 'bold', backgroundColor: '#2d6a4f', color: '#FFFFFF' }} />
            ) : success ? (
              <Chip label="Ready" color="success" size="small" sx={{ fontWeight: 'bold', backgroundColor: '#2d6a4f', color: '#FFFFFF' }} />
            ) : (
              <Chip label="Missing" color="default" size="small" variant="outlined" sx={{ borderStyle: 'dashed', color: 'rgba(44, 71, 72, 0.6)', borderColor: 'rgba(44, 71, 72, 0.4)' }} />
            )}
          </Box>

          {!isAvailable && !isLocked && (
            <Box sx={{ backgroundColor: 'rgba(240, 205, 127, 0.18)', p: 2, borderRadius: 3, border: '1.5px solid rgba(201, 151, 56, 0.35)', mb: 2.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#8c6210', display: 'block', mb: 0.5 }}>Suggested File Name Format</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontSize: '11px' }}>
                Format: <strong>{formatHint}</strong>
              </Typography>
              <Box sx={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(201, 151, 56, 0.25)', borderRadius: '8px', p: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.01)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, fontSize: '11.5px' }}>
                  {templateName}
                </Typography>
                <Button size="small" variant="text" onClick={onCopy} sx={{ minWidth: 0, p: 0.5, textTransform: 'none', color: '#8c6210', fontWeight: 'bold', fontSize: '12px', textDecoration: 'underline' }}>Copy</Button>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 2, borderRadius: 3, border: '1.5px dashed',
            borderColor: isLocked
              ? 'rgba(44, 71, 72, 0.1)'
              : (isAvailable ? '#2d6a4f' : (error ? '#d32f2f' : (success ? '#2d6a4f' : 'rgba(44, 71, 72, 0.2)'))),
            backgroundColor: isLocked
              ? 'rgba(44, 71, 72, 0.02)'
              : (isAvailable ? 'rgba(45, 106, 79, 0.02)' : (error ? 'rgba(211, 47, 47, 0.02)' : (success ? 'rgba(45, 106, 79, 0.02)' : 'transparent'))),
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px', textAlign: 'center'
          }}
        >
          {isLocked ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" sx={{ py: 1 }}>
              <Alert severity="info" sx={{ mb: 1, borderRadius: '8px', fontSize: '11.5px', textAlign: 'left', width: '100%', py: 0.5, backgroundColor: 'rgba(44, 71, 72, 0.05)', color: '#2C4748', '& .MuiAlert-icon': { color: '#2C4748' } }}>
                No amended version is available yet.
              </Alert>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>
                This Transaction Certificate does not contain any versioned amendments in the system.
              </Typography>
            </Box>
          ) : selectedFile ? (
            uploading ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
                <CircularProgress size={32} sx={{ color: '#2C4748', mb: 1.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', mb: 0.5 }}>Uploading certificate...</Typography>
                <Typography variant="caption" color="text.secondary">Please do not close this window</Typography>
              </Box>
            ) : (
              <Box width="100%">
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', wordBreak: 'break-all', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <UploadFileIcon sx={{ fontSize: 18, color: '#2C4748' }} /> {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</Typography>
                <Button variant="outlined" color="error" size="small" onClick={onRemove} sx={{ borderRadius: '12px', textTransform: 'none', px: 2.5, py: 0.3, fontWeight: 'bold' }}>Remove File</Button>
              </Box>
            )
          ) : isAvailable ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" sx={{ py: 1 }}>
              <Alert severity="success" sx={{ mb: 1, borderRadius: '8px', fontSize: '11.5px', textAlign: 'left', width: '100%', py: 0.5, backgroundColor: 'rgba(45, 106, 79, 0.05)', color: '#2d6a4f' }}>
                {alreadyUploadedMessage}
              </Alert>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>{alreadyUploadedNote}</Typography>
                {pdfUrl && (
                  <Tooltip title="View uploaded PDF" arrow>
                    <IconButton size="small" onClick={() => setViewSlotUrl(pdfUrl)} sx={{ color: '#2d6a4f', p: 0.3 }}>
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ) : (
            <Box width="100%">
              {error ? (
                <Box width="100%">
                  <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px', fontSize: '12px', textAlign: 'left' }}>{error}</Alert>
                  <Button size="small" variant="text" disabled={uploading} onClick={() => browsePdf(slot)} sx={{ textTransform: 'none', color: '#2C4748', fontWeight: 600, textDecoration: 'underline' }}>Choose matching file</Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '13px' }}>No file selected for Slot {slot}</Typography>
                  <Button size="small" variant="text" disabled={uploading} onClick={() => browsePdf(slot)} sx={{ textTransform: 'none', color: '#2C4748', fontWeight: 600, p: 0, textDecoration: 'underline' }}>Browse file directly</Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: 16, padding: 24, boxShadow: '0 12px 48px rgba(44, 71, 72, 0.12)', backgroundColor: '#FAFCFC' }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: '#2C4748', pb: 2, px: 0, pt: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <UploadFileIcon sx={{ fontSize: 24, color: '#2C4748' }} />
            Transaction Certificate PDF Multi-Slot Uploader
          </Box>
          <Tooltip title="Close" arrow>
            <span>
              <IconButton onClick={handleClose} disabled={uploading} size="small" sx={{ color: 'rgba(44, 71, 72, 0.5)', '&:hover': { color: '#2C4748' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 0, pb: 0 }}>
        {isMissingIssueDate && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Date of Issue Missing
            </Typography>
            <Typography variant="body2">
              PDF upload requires a valid date of issue. Please update the TC base details and return to upload.
            </Typography>
          </Alert>
        )}

        {/* Unified Master Drag & Drop */}
        <Box
          onDragEnter={(isUploadDisabled && !isBothAvailable) ? undefined : handleDrag}
          onDragOver={(isUploadDisabled && !isBothAvailable) ? undefined : handleDrag}
          onDragLeave={(isUploadDisabled && !isBothAvailable) ? undefined : handleDrag}
          onDrop={(isUploadDisabled && !isBothAvailable) ? undefined : handleDrop}
          onClick={() => { if (uploading || isUploadDisabled) return; browsePdf(); }}
          sx={{
            border: '2px dashed',
            borderColor: isMissingIssueDate
              ? 'rgba(237, 108, 2, 0.4)'
              : (isUploadDisabled ? 'rgba(45, 106, 79, 0.3)' : (dragActive ? '#2C4748' : 'rgba(44, 71, 72, 0.3)')),
            borderRadius: 4, py: 5, px: 3, textAlign: 'center',
            cursor: (uploading || (isUploadDisabled && !isBothAvailable)) ? 'not-allowed' : (isUploadDisabled ? 'default' : 'pointer'),
            opacity: uploading ? 0.6 : 1,
            pointerEvents: uploading ? 'none' : 'auto',
            backgroundColor: isMissingIssueDate
              ? 'rgba(237, 108, 2, 0.02)'
              : (isUploadDisabled ? 'rgba(45, 106, 79, 0.02)' : (dragActive ? 'rgba(44, 71, 72, 0.08)' : '#FFFFFF')),
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.02)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            mb: 4,
            '&:hover': {
              borderColor: (isUploadDisabled || isMissingIssueDate) ? undefined : '#2C4748',
              transform: (uploading || isUploadDisabled) ? 'none' : 'translateY(-1px)',
              boxShadow: (uploading || isUploadDisabled) ? '0 4px 16px rgba(0, 0, 0, 0.02)' : '0 8px 24px rgba(44, 71, 72, 0.08)',
              backgroundColor: (uploading || isUploadDisabled) ? undefined : 'rgba(44, 71, 72, 0.01)'
            }
          }}
        >
          <UploadFileIcon sx={{ fontSize: 56, color: isMissingIssueDate ? '#ed6c02' : (isUploadDisabled ? '#2d6a4f' : '#2C4748'), mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: isMissingIssueDate ? '#ed6c02' : (isUploadDisabled ? '#2d6a4f' : '#2C4748'), mb: 0.5 }}>
            {isMissingIssueDate
              ? "PDF Upload Unavailable — Issue Date Missing"
              : isBothAvailable
                ? "Both PDF Certificates Uploaded"
                : (isBaseAvailable && hasNoAmendments)
                  ? "Original PDF Certificate Already Uploaded"
                  : "Drag & Drop PDF file(s) here to Auto-Route"
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: isBothAvailable ? 2 : 0 }}>
            {isMissingIssueDate
              ? "A valid date of issue is required to generate the correct PDF filename. Please update the TC and return to upload."
              : isBothAvailable
                ? "Both certificates are on file. Click below to preview either document."
                : (isBaseAvailable && hasNoAmendments)
                  ? "No further uploads allowed as the Original PDF is available and no versioned amendments exist."
                  : "Or click to browse and select multiple files from your computer"
            }
          </Typography>
          {isBothAvailable && (
            <Box display="flex" justifyContent="center" gap={2} onClick={e => e.stopPropagation()}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => setViewSlotUrl(tcNoPdfUrl!)}
                sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#2d6a4f', color: '#2d6a4f', fontWeight: 600, '&:hover': { borderColor: '#1b4332', backgroundColor: 'rgba(45,106,79,0.05)' } }}
              >
                View Original
              </Button>
              {versionPdfViewUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setViewSlotUrl(versionPdfViewUrl)}
                  sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#2d6a4f', color: '#2d6a4f', fontWeight: 600, '&:hover': { borderColor: '#1b4332', backgroundColor: 'rgba(45,106,79,0.05)' } }}
                >
                  View Amended
                </Button>
              )}
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          {renderSlot(
            'A', isBaseAvailable, selectedFileA, errorA, successA,
            "Original Certificate (Base)",
            templateNameA,
            "tc_[cb-code]_[tc-number]_[date]",
            copyTemplateA,
            () => { setSelectedFileA(null); setSuccessA(false); setErrorA(null); },
            "Original Base PDF is already available.",
            isVersionAvailable ? "No further action required." : "You only need to upload the amended certificate in Slot B if applicable.",
            false,
            tcNoPdfUrl,
          )}
          {renderSlot(
            'B', isVersionAvailable, selectedFileB, errorB, successB,
            `Amended Certificate (Versioned ${versionSuffix})`,
            templateNameB,
            "tc_[cb-code]_[tc-number-versioned]_[date]",
            copyTemplateB,
            () => { setSelectedFileB(null); setSuccessB(false); setErrorB(null); },
            "Amended Certificate PDF is already available.",
            "No further action required.",
            hasNoAmendments,
            versionPdfViewUrl,
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 0, pb: 0, pt: 3 }}>
        <Button onClick={handleClose} disabled={uploading} sx={{ height: "40px", textTransform: 'none', color: '#2C4748', fontWeight: 600, px: 3 }}>Cancel</Button>
        <Tooltip title={isButtonDisabled ? getUploadButtonTooltip() : ""} arrow placement="top">
          <span>
            <StyledButton
              onClick={handleFileSubmit}
              disabled={isButtonDisabled}
              variant="contained"
              sx={{
                height: "40px",
                backgroundColor: '#F0CD7F',
                color: '#1C1C1C',
                '&:hover': { backgroundColor: '#E0B86F' },
                '&.Mui-disabled': { backgroundColor: 'rgba(240, 205, 127, 0.4)', color: 'rgba(28, 28, 28, 0.4)' }
              }}
            >
              {uploading ? <CircularProgress size={20} color="inherit" /> : "Upload Files"}
            </StyledButton>
          </span>
        </Tooltip>
      </DialogActions>

      {/* Nested PDF viewer — opens when user clicks the eye icon on an uploaded slot */}
      {viewSlotUrl && (
        <Dialog
          open={!!viewSlotUrl}
          onClose={(_e, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setViewSlotUrl(null);
          }}
          disableEscapeKeyDown
          maxWidth="lg"
          fullWidth
          PaperProps={{ style: { height: '90vh', borderRadius: 12, overflow: 'hidden' } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, borderBottom: '1px solid rgba(44,71,72,0.1)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2C4748' }}>PDF Preview</Typography>
            <IconButton size="small" onClick={() => setViewSlotUrl(null)} sx={{ color: 'rgba(44,71,72,0.5)', '&:hover': { color: '#2C4748' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
            <iframe
              src={viewSlotUrl}
              title="PDF Preview"
              width="100%"
              style={{ flex: 1, border: 'none', minHeight: '78vh' }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};
